import React, { useState, useEffect, KeyboardEvent } from 'react';
import {
  Box,
  Paper,
  Stack,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Collapse,
  Typography,
  Fade,
  useTheme,
  alpha,
  Autocomplete,
} from '@mui/material';
import { CalendarToday as CalendarIcon } from '@mui/icons-material';
import { motion } from 'motion/react';
import { ExpandableFieldItem, SchedulingData } from './ExpandableField';
import { useForms } from '../../forms';
import type { FormWithRevisionSummary } from '@store/generated/forms';
import { useDebounce } from '../../../hooks/useDebounce';
import { EXPANDABLE_ITEM_HEIGHT } from '../constants';
import { formatAppointmentDuration, isImmediateTiming } from '../../../utils/durationUtils';

interface SchedulingFormProps {
  mode: 'create' | 'edit';
  initialData?: ExpandableFieldItem;
  isFormType?: boolean;
  onSubmit: (data: SchedulingData) => void;
  onCancel: () => void;
}

/**
 * Adapter function to transform V2 forms for autocomplete usage
 */
function transformV2FormsForAutocomplete(v2Forms: FormWithRevisionSummary[]): Array<{
  form_id: string;
  title: string;
  description: string;
}> {
  return v2Forms.map(item => ({
    form_id: item.form.id || '',
    title: item.current_revision?.title || 'Untitled Form',
    description: item.current_revision?.description || '',
  }));
}

// Single source of truth for timing presets
const TIMING_PRESETS = {
  forms: [
    { display: '2 hours before appointment', iso: '-PT2H' },
    { display: '1 day before appointment', iso: '-P1D' },
    { display: '1 week before appointment', iso: '-P7D' },
    { display: '5 minutes after appointment', iso: 'PT5M' },
  ],
  reminders: [
    { display: '2 hours before appointment', iso: '-PT2H' },
    { display: '1 day before appointment', iso: '-P1D' },
    { display: '1 week before appointment', iso: '-P7D' },
  ],
};

const SchedulingForm: React.FC<SchedulingFormProps> = ({
  mode,
  initialData,
  isFormType = false,
  onSubmit,
  onCancel,
}) => {
  const theme = useTheme();
  const [isExiting, setIsExiting] = useState(false);
  const [pendingAction, setPendingAction] = useState<{
    type: 'submit' | 'cancel';
    data?: SchedulingData;
  } | null>(null);

  // Search state for forms autocomplete
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Fetch forms with search when it's a form type
  const { forms: rawV2Forms, isLoading: formsLoading } = useForms(
    {
      pageSize: 20,
      search: debouncedSearchQuery || undefined,
      // includeDrafts: false (default - no drafts)
    },
    { skip: !isFormType },
  );

  // Transform V2 forms for autocomplete compatibility
  const availableForms = transformV2FormsForAutocomplete(rawV2Forms);

  const [schedulingData, setSchedulingData] = useState<{
    name: string;
    timing: string;
    customTiming: string;
    customQuantity: number;
    customUnit: string;
    customDirection: string;
    formId?: string;
  }>({
    name: isFormType ? '' : 'Appointment Reminder',
    timing: '1 day before appointment',
    customTiming: '',
    customQuantity: 1,
    customUnit: 'days',
    customDirection: 'before',
    formId: '',
  });

  const [customTimingError, setCustomTimingError] = useState<string>('');

  // Separate string state for quantity display to allow empty field
  const [customQuantityDisplay, setCustomQuantityDisplay] = useState(
    schedulingData.customQuantity.toString(),
  );

  // Helper function to validate if timing is within 30 days
  const validateTiming = (quantity: number, unit: string): boolean => {
    let totalDays = 0;

    switch (unit) {
      case 'minutes':
        totalDays = quantity / (60 * 24);
        break;
      case 'hours':
        totalDays = quantity / 24;
        break;
      case 'days':
        totalDays = quantity;
        break;
      case 'weeks':
        totalDays = quantity * 7;
        break;
    }

    return totalDays <= 30;
  };

  // Helper function to convert guided inputs to ISO 8601 duration format
  const convertGuidedTimingToISO = (quantity: number, unit: string, direction: string): string => {
    let isoString = '';

    // Build ISO 8601 duration
    if (unit === 'minutes') {
      isoString = `PT${quantity}M`;
    } else if (unit === 'hours') {
      isoString = `PT${quantity}H`;
    } else if (unit === 'days') {
      isoString = `P${quantity}D`;
    } else if (unit === 'weeks') {
      isoString = `P${quantity * 7}D`;
    }

    // Add negative sign for "before"
    if (direction === 'before') {
      isoString = `-${isoString}`;
    }

    return isoString;
  };

  // Helper function to parse timing back to guided inputs
  const parseTimingToGuidedInputs = (
    isoDuration: string | undefined,
    presets: typeof TIMING_PRESETS.forms,
  ) => {
    if (!isoDuration) return null;

    // Check if it's immediate timing
    if (isImmediateTiming(isoDuration)) {
      return { isCustom: false };
    }

    // Convert ISO to display format first
    const displayTiming = formatAppointmentDuration(isoDuration);

    // Check if it matches a preset option by comparing the formatted output
    const presetDisplayValues = presets.map(preset => preset.display);

    if (presetDisplayValues.includes(displayTiming)) {
      return { isCustom: false };
    }

    // If not a preset, parse as custom
    const isNegative = isoDuration.startsWith('-');
    const direction = isNegative ? 'before' : 'after';
    const cleanISO = isNegative ? isoDuration.slice(1) : isoDuration;

    const match = cleanISO.match(/^P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?)?$/);
    if (match) {
      const [, days, hours, minutes] = match;

      if (days) {
        const dayCount = parseInt(days);
        if (dayCount % 7 === 0 && dayCount > 7) {
          return {
            isCustom: true,
            quantity: dayCount / 7,
            unit: 'weeks',
            direction,
          };
        } else {
          return {
            isCustom: true,
            quantity: dayCount,
            unit: 'days',
            direction,
          };
        }
      } else if (hours) {
        return {
          isCustom: true,
          quantity: parseInt(hours),
          unit: 'hours',
          direction,
        };
      } else if (minutes) {
        return {
          isCustom: true,
          quantity: parseInt(minutes),
          unit: 'minutes',
          direction,
        };
      }
    }

    return { isCustom: false };
  };

  // Initialize form data when initialData changes (for edit mode)
  useEffect(() => {
    if (mode === 'edit' && initialData) {
      // Use the offset field (ISO 8601 format) for parsing
      const isoOffset = initialData.offset || '';
      const currentPresets = TIMING_PRESETS[isFormType ? 'forms' : 'reminders'];
      const parsed = parseTimingToGuidedInputs(isoOffset, currentPresets);

      if (parsed?.isCustom) {
        const quantity = parsed.quantity || 1;
        setSchedulingData({
          name: isFormType ? initialData.name : 'Appointment Reminder',
          timing: 'custom',
          customTiming: '',
          customQuantity: quantity,
          customUnit: parsed.unit || 'days',
          customDirection: parsed.direction || 'before',
        });
        setCustomQuantityDisplay(quantity.toString());
      } else {
        // Convert ISO to display format for preset options
        const displayTiming = formatAppointmentDuration(isoOffset);
        setSchedulingData({
          name: isFormType ? initialData.name : 'Appointment Reminder',
          timing: displayTiming,
          customTiming: '',
          customQuantity: 1,
          customUnit: 'days',
          customDirection: 'before',
        });
      }
    }
  }, [mode, initialData]);

  // Derive timing options from TIMING_PRESETS
  const currentPresets = TIMING_PRESETS[isFormType ? 'forms' : 'reminders'];
  const timingOptions = [...currentPresets.map(preset => preset.display), 'custom'];

  const unitOptions = [
    { value: 'minutes', label: 'minutes' },
    { value: 'hours', label: 'hours' },
    { value: 'days', label: 'days' },
    { value: 'weeks', label: 'weeks' },
  ];

  const directionOptions = [
    { value: 'before', label: 'before' },
    { value: 'after', label: 'after' },
  ];

  const handleSubmit = () => {
    if (schedulingData.name && schedulingData.timing) {
      let finalTiming: string;
      let customTiming: string;

      if (schedulingData.timing === 'custom') {
        // Validate timing is within 30 days
        if (!validateTiming(schedulingData.customQuantity, schedulingData.customUnit)) {
          setCustomTimingError('Scheduling window cannot exceed 30 days');
          return;
        }

        // Generate timing string from guided inputs
        const isoTiming = convertGuidedTimingToISO(
          schedulingData.customQuantity,
          schedulingData.customUnit,
          schedulingData.customDirection,
        );
        finalTiming = `${schedulingData.customQuantity} ${schedulingData.customUnit} ${schedulingData.customDirection} appointment`;
        customTiming = isoTiming;
      } else {
        // Convert preset timing to ISO 8601 format
        const presetToISOMap = Object.fromEntries(
          currentPresets.map(preset => [preset.display, preset.iso]),
        );

        finalTiming = schedulingData.timing;
        customTiming = presetToISOMap[schedulingData.timing] || schedulingData.timing;
      }

      // Store the data and trigger exit animation
      setPendingAction({
        type: 'submit',
        data: {
          name: schedulingData.name,
          timing: finalTiming,
          customTiming,
          formId: schedulingData.formId,
        },
      });
      setIsExiting(true);
    }
  };

  const handleCancel = () => {
    setPendingAction({ type: 'cancel' });
    setIsExiting(true);
  };

  const handleKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && schedulingData.name && schedulingData.timing) {
      event.preventDefault();
      handleSubmit();
    }
  };

  return (
    <motion.div
      initial={{ height: EXPANDABLE_ITEM_HEIGHT, opacity: 1 }} // Start at button height
      animate={{
        height: isExiting ? EXPANDABLE_ITEM_HEIGHT : 'auto',
        opacity: isExiting ? 0 : 1,
      }}
      transition={{
        height: { duration: 0.3, ease: isExiting ? 'easeIn' : 'easeOut' },
        opacity: {
          duration: isExiting ? 0.15 : 0,
          delay: isExiting ? 0.15 : 0,
        },
      }}
      onAnimationComplete={() => {
        if (isExiting && pendingAction) {
          if (pendingAction.type === 'submit' && pendingAction.data) {
            onSubmit(pendingAction.data);
          } else if (pendingAction.type === 'cancel') {
            onCancel();
          }
        }
      }}
      style={{ overflow: 'hidden' }}
    >
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderStyle: 'dashed',
          borderColor: 'primary.main',
          backgroundColor: alpha(theme.palette.primary.main, 0.04),
        }}
      >
        <Stack spacing={2}>
          {/* Name/Type Selection */}
          {isFormType ? (
            // Forms Autocomplete with search
            <Autocomplete
              fullWidth
              size="small"
              options={availableForms || []}
              getOptionLabel={option => option.title}
              value={availableForms?.find(form => form.title === schedulingData.name) || null}
              onChange={(event, value) => {
                setSchedulingData(prev => ({
                  ...prev,
                  name: value?.title || '',
                  formId: value?.form_id || '',
                }));
              }}
              onInputChange={(event, newInputValue) => {
                setSearchQuery(newInputValue);
              }}
              loading={formsLoading}
              loadingText="Searching forms..."
              noOptionsText="No forms found"
              renderInput={params => (
                <TextField {...params} label="Select Form" onKeyDown={handleKeyPress} />
              )}
              renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                return (
                  <Box component="li" key={key} {...otherProps}>
                    <Typography variant="body2">{option.title}</Typography>
                  </Box>
                );
              }}
            />
          ) : (
            // Reminders - show fixed "Appointment Reminder" text
            <TextField
              value="Appointment Reminder"
              label="Reminder Type"
              size="small"
              fullWidth
              disabled
              InputProps={{
                readOnly: true,
              }}
              onKeyDown={handleKeyPress}
            />
          )}

          {/* Timing Selection */}
          <FormControl fullWidth size="small">
            <InputLabel>{isFormType ? 'Send' : 'Remind'}</InputLabel>
            <Select
              value={schedulingData.timing}
              label={isFormType ? 'Send' : 'Remind'}
              onChange={e =>
                setSchedulingData(prev => ({
                  ...prev,
                  timing: e.target.value as string,
                }))
              }
              onKeyDown={handleKeyPress}
            >
              {timingOptions.map(option => (
                <MenuItem key={option} value={option}>
                  {option === 'custom' ? 'Custom...' : option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Custom Timing Input - Guided Fields */}
          <Collapse in={schedulingData.timing === 'custom'}>
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                Custom Timing
              </Typography>

              {/* Guided Input Row */}
              <Stack direction="row" spacing={1} alignItems="center">
                {/* Quantity */}
                <TextField
                  type="number"
                  size="small"
                  value={customQuantityDisplay}
                  onChange={e => {
                    const value = e.target.value;
                    setCustomQuantityDisplay(value);

                    // If empty, don't update the actual data yet
                    if (value === '') {
                      setCustomTimingError(''); // Clear error for empty state
                      return;
                    }

                    const parsedValue = parseInt(value);
                    if (isNaN(parsedValue) || parsedValue < 1) {
                      return; // Don't update if invalid
                    }

                    const newQuantity = Math.min(999, parsedValue);
                    setSchedulingData(prev => ({
                      ...prev,
                      customQuantity: newQuantity,
                    }));

                    // Validate timing
                    if (!validateTiming(newQuantity, schedulingData.customUnit)) {
                      setCustomTimingError('Scheduling window cannot exceed 30 days');
                    } else {
                      setCustomTimingError('');
                    }
                  }}
                  onBlur={() => {
                    // Reset to valid value if empty or invalid
                    if (!customQuantityDisplay || parseInt(customQuantityDisplay) < 1) {
                      setCustomQuantityDisplay('1');
                      setSchedulingData(prev => ({
                        ...prev,
                        customQuantity: 1,
                      }));
                    }
                  }}
                  onFocus={e => {
                    // Select all text when field is focused
                    e.target.select();
                  }}
                  error={!!customTimingError}
                  inputProps={{ min: 1, max: 999 }}
                  sx={{ width: 80 }}
                />

                {/* Unit */}
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <Select
                    value={schedulingData.customUnit}
                    onChange={e => {
                      const newUnit = e.target.value as string;
                      setSchedulingData(prev => ({
                        ...prev,
                        customUnit: newUnit,
                      }));

                      // Validate timing - use the actual quantity, not display
                      const actualQuantity =
                        customQuantityDisplay === '' ? 1 : parseInt(customQuantityDisplay) || 1;
                      if (!validateTiming(actualQuantity, newUnit)) {
                        setCustomTimingError('Scheduling window cannot exceed 30 days');
                      } else {
                        setCustomTimingError('');
                      }
                    }}
                  >
                    {unitOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Direction */}
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <Select
                    value={schedulingData.customDirection}
                    onChange={e =>
                      setSchedulingData(prev => ({
                        ...prev,
                        customDirection: e.target.value as string,
                      }))
                    }
                  >
                    {directionOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Static "appointment" text */}
                <Typography variant="body2" color="text.secondary">
                  appointment
                </Typography>
              </Stack>

              {/* Error message */}
              {customTimingError && (
                <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                  {customTimingError}
                </Typography>
              )}
            </Stack>
          </Collapse>

          {/* Action Buttons */}
          <Stack direction="row" spacing={1} sx={{ pt: 1 }}>
            <Button
              variant="contained"
              fullWidth
              startIcon={<CalendarIcon />}
              onClick={handleSubmit}
              disabled={!schedulingData.name || !schedulingData.timing || !!customTimingError}
            >
              {mode === 'edit' ? 'Update' : 'Schedule'}
            </Button>
            <Button variant="outlined" fullWidth onClick={handleCancel}>
              Cancel
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </motion.div>
  );
};

export default SchedulingForm;
